import type * as api from '@gitbook/api-client';
import { compareSiteTypes, getRequiredSiteTypeForFeature } from '@gitbook/app-pricing';
import type { BadgeKind, BadgeProps } from '@gitbook/spine-react';

import type { UseUpgradeAction, FeatureUpgradeAction } from './types';

/**
 * Maps UseUpgradeAction['kind'] to BadgeKind values where possible.
 */
export function mapUpgradeActionKindToBadgeKind(
    buttonKind: UseUpgradeAction['kind'] | undefined
): BadgeKind | undefined {
    switch (buttonKind) {
        case 'primary':
            return 'upgrade';
        case 'secondary':
        case 'danger':
        case 'upgrade':
            return buttonKind;
    }

    return undefined;
}

/**
 * Check if a site upgrade is required for a site to use the given feature.
 *
 * @param site - The site to check.
 * @param featureKey - The feature to check.
 * @returns True if an upgrade is required, false otherwise.
 */
export function isSiteUpgradeRequiredForFeature({
    site,
    featureKey,
}: {
    site: api.Site;
    featureKey: api.SiteFeatureKey;
}): boolean {
    const currentSiteType = site.appliedType ?? site.type;
    const requiredSiteType = getRequiredSiteTypeForFeature({ site, featureKey });

    return requiredSiteType ? compareSiteTypes(requiredSiteType, currentSiteType) > 0 : true;
}

/** Build badge props from a feature upgrade action. */
export function getBadgePropsFromUpgradeAction(options: {
    upgrade: FeatureUpgradeAction;
}): BadgeProps {
    return {
        kind: mapUpgradeActionKindToBadgeKind(options.upgrade.kind),
        size: 'small',
        icon: options.upgrade.leadingIcon,
        label: options.upgrade.badgeLabel ?? options.upgrade.label,
        thin: true,
    };
}
